import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, ChevronDown, Plus, RotateCw, Pencil, Trash2, Send, History, XCircle, CheckCheck, ExternalLink } from 'lucide-react';
import { api } from '@/api/client';
import type { GetResourcesResp, Resource } from '@/api/types';
import { cn } from '@/lib/utils';
import UsagesList from '@/components/resources/UsagesList';
import ValuesList from '@/components/resources/ValuesList';

export default function ResourcesPage() {
  const [expandedName, setExpandedName] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['resources'],
    queryFn: () => api<GetResourcesResp>('/v1/resources'),
    refetchOnWindowFocus: true,
  });

  const deleteMutation = useMutation({
    mutationFn: (name: string) =>
      api<unknown>(`/v1/resources/${encodeURIComponent(name)}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['resources'] }),
    onError: (err) =>
      alert(`Delete failed: ${err instanceof Error ? err.message : String(err)}`),
  });

  // Fire-and-forget ack for a single resource. The user is navigating to
  // the legacy productivity page anyway, so we don't block on this —
  // the count resets on the next load of /v1/resources.
  const ackMutation = useMutation({
    mutationFn: (name: string) =>
      api<unknown>(
        `/v1/resources/${encodeURIComponent(name)}/productivity-gap/ack`,
        { method: 'POST' },
      ),
  });

  const ackAllMutation = useMutation({
    mutationFn: () =>
      api<unknown>('/v1/productivity-gap/ack-all', { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['resources'] }),
    onError: (err) =>
      alert(`Mark all seen failed: ${err instanceof Error ? err.message : String(err)}`),
  });

  const list = data?.resources ?? [];
  const totalUnseen = list.reduce(
    (sum, r) => sum + (r.productivityGapUnseenCount ?? 0),
    0,
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Resources</h1>
        <div className="flex gap-2">
          {totalUnseen > 0 && (
            <button
              onClick={() => ackAllMutation.mutate()}
              disabled={ackAllMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border border-destructive/40 text-destructive hover:bg-destructive/10 disabled:opacity-50 transition-colors"
              title={`Mark ${totalUnseen} unseen productivity-gap item${totalUnseen === 1 ? '' : 's'} as seen`}
            >
              <CheckCheck className="h-4 w-4" />
              Mark all seen
              <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-destructive text-destructive-foreground text-[11px] font-medium">
                {totalUnseen}
              </span>
            </button>
          )}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border hover:bg-muted disabled:opacity-50 transition-colors"
          >
            <RotateCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
            Refresh
          </button>
          <Link
            to="/resources/new"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
          >
            <Plus className="h-4 w-4" />
            New Resource
          </Link>
        </div>
      </div>

      {isLoading && (
        <div className="py-12 text-center text-muted-foreground text-sm">Loading resources…</div>
      )}

      {error && (
        <div className="py-3 px-4 rounded-md border border-destructive/30 bg-destructive/5 text-destructive text-sm">
          Failed to load resources: {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      {!isLoading && !error && (
        <div className="border rounded-lg overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="text-left">
                <th className="w-10" aria-label="expand" />
                <th className="px-3 py-2.5 font-medium">Name / Type</th>
                <th className="px-3 py-2.5 font-medium">Value</th>
                <th className="px-3 py-2.5 font-medium">Description</th>
                <th className="px-3 py-2.5 font-medium">Usage</th>
                <th className="px-3 py-2.5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-16 text-center text-muted-foreground">
                    No resources yet. Click{' '}
                    <strong className="text-foreground">New Resource</strong> to create one.
                  </td>
                </tr>
              )}
              {list.map((r) => (
                <ResourceRow
                  key={r.name}
                  resource={r}
                  expanded={expandedName === r.name}
                  onToggle={() =>
                    setExpandedName(expandedName === r.name ? null : r.name)
                  }
                  onDelete={() => {
                    if (confirm(`Delete resource "${r.name}"?`)) {
                      deleteMutation.mutate(r.name);
                    }
                  }}
                  onAckProductivityGap={() => ackMutation.mutate(r.name)}
                  deleting={
                    deleteMutation.isPending && deleteMutation.variables === r.name
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface ResourceRowProps {
  resource: Resource;
  expanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onAckProductivityGap: () => void;
  deleting: boolean;
}

function ResourceRow({
  resource,
  expanded,
  onToggle,
  onDelete,
  onAckProductivityGap,
  deleting,
}: ResourceRowProps) {
  const qName = encodeURIComponent(resource.name);
  const unseen = resource.productivityGapUnseenCount ?? 0;

  return (
    <>
      <tr
        className={cn(
          'border-t cursor-pointer transition-colors',
          expanded ? 'bg-muted/20' : 'hover:bg-muted/10',
        )}
        onClick={onToggle}
      >
        <td className="pl-3 text-muted-foreground">
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </td>
        <td className="px-3 py-3">
          <div className="font-medium">{resource.name}</div>
          <div className="text-xs text-muted-foreground">{resource.type}</div>
        </td>
        <td className="px-3 py-3 font-mono text-xs break-all max-w-[400px]">
          <ValueCell resource={resource} />
          {resource.softLimit && (
            <div className="mt-0.5 inline-block rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground font-sans">
              soft limit: {resource.softLimit}
            </div>
          )}
        </td>
        <td className="px-3 py-3 text-muted-foreground">
          {resource.description || <span className="italic">—</span>}
        </td>
        <td
          className="px-3 py-3 font-mono text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          <Link
            to={`/resources/${qName}/usages`}
            target="_blank"
            rel="noopener"
            className="text-primary hover:underline"
            title="View current allocations (opens in new tab)"
          >
            {resource.usage ?? '0'}
          </Link>
        </td>
        <td
          className="px-3 py-3 text-right"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="inline-flex items-center gap-1">
            {resource.type === 'String' ? (
              <button
                type="button"
                disabled
                className="inline-flex items-center justify-center p-1.5 rounded opacity-40 cursor-not-allowed"
                title="Not allocatable for String type"
                aria-label="Manual request unavailable for String type"
              >
                <Send className="h-4 w-4" />
              </button>
            ) : (
              <Link
                to={`/resources/${qName}/manual-request`}
                className="inline-flex items-center justify-center p-1.5 rounded hover:bg-muted transition-colors"
                title="Manual request"
                aria-label="Submit manual request"
              >
                <Send className="h-4 w-4" />
              </Link>
            )}
            <Link
              to={`/resources/${qName}/modify`}
              className="inline-flex items-center justify-center p-1.5 rounded hover:bg-muted transition-colors"
              title="Modify resource"
              aria-label="Modify resource"
            >
              <Pencil className="h-4 w-4" />
            </Link>
            <button
              type="button"
              onClick={onDelete}
              disabled={deleting}
              className="inline-flex items-center justify-center p-1.5 rounded text-destructive hover:bg-destructive/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title={deleting ? 'Deleting…' : 'Delete resource'}
              aria-label={deleting ? 'Deleting resource' : 'Delete resource'}
            >
              <Trash2 className={cn('h-4 w-4', deleting && 'animate-pulse')} />
            </button>
            <span className="mx-0.5 h-5 w-px bg-border" aria-hidden="true" />
            <Link
              to={`/resources/${qName}/past-usages`}
              className="inline-flex items-center justify-center p-1.5 rounded hover:bg-muted transition-colors"
              title="View past usage history"
              aria-label="View past usages"
            >
              <History className="h-4 w-4" />
            </Link>
            <Link
              to={`/resources/${qName}/productivity-gaps`}
              onClick={() => {
                // Pre-ack so the badge clears immediately. The destination
                // page also acks on mount; both calls are idempotent.
                if (unseen > 0) onAckProductivityGap();
              }}
              className={cn(
                'relative inline-flex items-center justify-center p-1.5 rounded transition-colors',
                unseen > 0
                  ? 'text-destructive hover:bg-destructive/10'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
              title={
                unseen > 0
                  ? `${unseen} unseen warning${unseen === 1 ? '' : 's'} or rejection${unseen === 1 ? '' : 's'} — click to view`
                  : 'View productivity gap (warnings and rejected requests)'
              }
              aria-label={
                unseen > 0
                  ? `View productivity gap (${unseen} unseen)`
                  : 'View productivity gap'
              }
            >
              <XCircle className="h-4 w-4" />
              {unseen > 0 && (
                <>
                  <span
                    className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-destructive animate-pulse"
                    aria-hidden="true"
                  />
                  <span className="sr-only">{unseen} unseen</span>
                </>
              )}
            </Link>
          </div>
        </td>
      </tr>

      {expanded && (
        <tr className="bg-muted/5">
          <td />
          {/*
            Auto-layout firewall:
            - The cell sets `width: 1px` to bias the browser away from
              treating its content's intrinsic width as the cell's
              preferred width.
            - The inner wrapper uses `width: 0; min-width: 100%`: its
              CSS preferred width is 0 (so it contributes nothing to
              the spanned columns' auto-layout), but `min-width: 100%`
              forces the *used* width to fill the cell visually.
            - `overflow-hidden` clips any content that's wider than
              the cell — which then falls back on the inner components
              (ValuesList / UsagesList) wrapping their own content.
            Effect: the resources table's column widths are determined
            only by the data rows; expanding a row (no matter how wide
            its YAML / cluster IDs / project names are) leaves cols 2-6
            of the outer table exactly where they were.
          */}
          <td colSpan={5} className="p-0" style={{ width: '1px' }}>
            <div
              className="overflow-hidden"
              style={{ width: 0, minWidth: '100%' }}
            >
              <div className="p-4 space-y-3">
                {(resource.type === 'StringList' ||
                  resource.type === 'Collection') && (
                  <>
                    <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                      Values
                    </div>
                    <ValuesList
                      items={
                        resource.type === 'StringList'
                          ? parseStringList(resource.value)
                          : parseCollection(resource.value)
                      }
                    />
                  </>
                )}
                <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                  {resource.type === 'StringList' ||
                  resource.type === 'Collection'
                    ? 'Usages'
                    : 'Current Allocations'}
                </div>
                <UsagesList resourceName={resource.name} />
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// Measurement resources have two distinct output shapes (mirroring
// storage.OutputTypeJSON / OutputTypeSingle):
//   - License-usage:  /v1/resources populates `license_usage` with totals and
//                     availability; we surface that as the visible value.
//   - Single value:   only `measurementMaxValue` (the configured max) comes
//                     through the API today. The current reading lives in
//                     storage's LastMeasurements map and is not yet exposed —
//                     tracked in docs/ui-v2-backlog.md.
function ValueCell({ resource }: { resource: Resource }) {
  if (resource.type === 'Measurement') {
    const lic = resource.license_usage;
    const max = resource.measurementMaxValue;
    const totalUsage = lic?.total_usage ?? 0;
    const total = lic?.total ?? 0;
    const nonBatch = lic?.non_batch_usage ?? 0;
    // pkg/storage/output.go:52 annotates `Available` as "the measured value".
    // For OutputTypeJSON measurements, license_usage.available is the latest
    // reading — same field the v1 indexhandler shows in its `measured:` line.
    const measured = lic?.available;
    // Tables guarantee column alignment regardless of any CSS
    // weirdness. Inline styles on the wrapper guarantee the two blocks
    // sit side-by-side. No flex/grid utility-class dependencies.
    const labelCellStyle = {
      paddingRight: '0.75rem',
      verticalAlign: 'top',
    } as const;
    const valueCellStyle = { verticalAlign: 'top' } as const;
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '1.5rem',
        }}
      >
        {/* Left block — what v1 calls the "Value" column. */}
        <table style={{ borderCollapse: 'collapse' }}>
          <tbody>
            {measured != null && (
              <tr>
                <td
                  style={labelCellStyle}
                  className="text-muted-foreground font-sans"
                >
                  measured:
                </td>
                <td style={valueCellStyle} className="font-mono">
                  {measured}
                </td>
              </tr>
            )}
            <tr>
              <td
                style={labelCellStyle}
                className="text-muted-foreground font-sans"
              >
                max:
              </td>
              <td style={valueCellStyle} className="font-mono">
                {max != null && max >= 0 ? max : '—'}
              </td>
            </tr>
          </tbody>
        </table>
        {/* Right block — what v1 calls the "License Usage" column. Hidden
            for OutputTypeSingle measurements. Wrapped in a Link so the block
            is clickable; opens the v2 license-summary detail page in a new
            tab (which auto-runs a live measurement, mirroring v1's
            /licenseusagesform behaviour). Labels match v1 defaults;
            customized RM_LABEL_* env vars are backlogged. */}
        {lic && (
          <Link
            to={`/resources/${encodeURIComponent(resource.name)}/license-summary`}
            target="_blank"
            rel="noopener"
            onClick={(e) => e.stopPropagation()}
            title="View license summary (opens in new tab; runs live measurement)"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              borderLeft: '1px solid hsl(var(--border))',
              paddingLeft: '1rem',
              paddingRight: '0.5rem',
            }}
            className="rounded hover:bg-muted/40 group transition-colors"
          >
            <table
              style={{
                borderCollapse: 'collapse',
                fontSize: '11px',
              }}
            >
              <tbody>
                <tr>
                  <td
                    style={labelCellStyle}
                    className="text-muted-foreground font-sans"
                  >
                    RM Usage
                  </td>
                  <td style={valueCellStyle} className="font-mono">
                    {totalUsage - nonBatch}
                  </td>
                </tr>
                <tr>
                  <td
                    style={labelCellStyle}
                    className="text-muted-foreground font-sans"
                  >
                    Non-RM Usage
                  </td>
                  <td style={valueCellStyle} className="font-mono">
                    {nonBatch}
                  </td>
                </tr>
                <tr>
                  <td
                    style={labelCellStyle}
                    className="text-muted-foreground font-sans"
                  >
                    Total Licenses
                  </td>
                  <td style={valueCellStyle} className="font-mono">
                    {total}
                  </td>
                </tr>
              </tbody>
            </table>
            <ExternalLink
              className="h-3 w-3 shrink-0 text-muted-foreground/60 group-hover:text-primary transition-colors"
              aria-hidden
            />
          </Link>
        )}
      </div>
    );
  }

  if (resource.type === 'String') {
    let decoded = resource.value;
    try {
      decoded = atob(resource.value);
    } catch {
      /* fall back to raw */
    }
    return <div>{decoded || '—'}</div>;
  }

  if (resource.type === 'StringList' || resource.type === 'Collection') {
    const items =
      resource.type === 'StringList'
        ? parseStringList(resource.value)
        : parseCollection(resource.value);
    if (items.length === 0) return <div>—</div>;
    return (
      <div className="font-sans text-muted-foreground">
        {items.length} item{items.length === 1 ? '' : 's'}
      </div>
    );
  }

  return <div>{resource.value || '—'}</div>;
}

// StringList values are space-joined on the wire (see
// pkg/storage/stringlist.go: StringListAmountToRequestString joins with " ").
// Tolerate runs of whitespace and trim, so display stays useful even if a
// resource was created with odd formatting.
export function parseStringList(raw: string): string[] {
  if (!raw) return [];
  return raw.split(/\s+/).filter((s) => s.length > 0);
}

// Collection values arrive base64-encoded — see
// internal/db/resource/converter/collectionconverter.go:34 — wrapping either
// a JSON array ("[{...},...]") or a YAML sequence ("- foo: 1\n  bar: 2\n- ...").
// We decode first, then sniff the format: JSON array → JSON.parse and
// pretty-print each element; otherwise treat it as YAML and split on
// column-0 "- " markers, dedenting the 2-space continuation indent so each
// item reads as a clean YAML block. A real YAML parser would be more
// accurate, but everything emitted by pkg/collection's converter has the
// canonical 2-space block layout this heuristic expects.
export function parseCollection(raw: string): string[] {
  if (!raw) return [];
  let decoded = raw;
  try {
    decoded = atob(raw);
  } catch {
    /* not base64 — treat raw as already-decoded */
  }
  const trimmed = decoded.trim();
  if (!trimmed) return [];

  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) =>
          typeof item === 'string' ? item : JSON.stringify(item, null, 2),
        );
      }
    } catch {
      /* fall through to YAML handling */
    }
  }

  const lines = trimmed.split('\n');
  const items: string[] = [];
  let current: string[] = [];
  let started = false;
  for (const line of lines) {
    if (/^-(\s|$)/.test(line)) {
      if (started) items.push(current.join('\n').trimEnd());
      const rest = line.replace(/^-\s?/, '');
      current = rest.length > 0 ? [rest] : [];
      started = true;
    } else if (started) {
      // Dedent the canonical 2-space block-sequence indent so nested
      // structures still indent relative to their item.
      current.push(line.replace(/^ {2}/, ''));
    }
  }
  if (started) items.push(current.join('\n').trimEnd());
  if (items.length > 0) return items;

  return [trimmed];
}
